import { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, Loader2, Sparkles, Upload, RotateCcw, ArrowRight, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const SEVERITY_TONE = {
  "Minor":       { bg: "bg-emerald-100 dark:bg-emerald-500/15", text: "text-emerald-700 dark:text-emerald-300", accent: "border-emerald-500" },
  "Moderate":    { bg: "bg-amber-100 dark:bg-amber-500/15",     text: "text-amber-700 dark:text-amber-300",     accent: "border-amber-500" },
  "Severe":      { bg: "bg-orange-100 dark:bg-orange-500/15",   text: "text-orange-700 dark:text-orange-300",   accent: "border-orange-500" },
  "Total Loss":  { bg: "bg-rose-100 dark:bg-rose-500/15",       text: "text-rose-700 dark:text-rose-300",       accent: "border-rose-500" },
};

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

async function compress(file, maxSide = 1400, quality = 0.82) {
  const dataUrl = await fileToBase64(file);
  const img = new Image();
  img.src = dataUrl;
  await new Promise((res) => { img.onload = res; });
  const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const cv = document.createElement("canvas");
  cv.width = w; cv.height = h;
  cv.getContext("2d").drawImage(img, 0, 0, w, h);
  return cv.toDataURL("image/jpeg", quality);
}

export default function InstantDemoWidget() {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  const runDemo = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image (JPG or PNG)");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const compressed = await compress(file);
      setPreview(compressed);
      const r = await api.post("/ai/vision/damage-estimate-demo", { image_base64: compressed });
      setResult(r.data);
    } catch (e) {
      const msg = e.response?.data?.detail || "Demo unavailable — please try again.";
      toast.error(typeof msg === "string" ? msg : "Demo unavailable");
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setPreview(null); setResult(null); if (inputRef.current) inputRef.current.value = ""; };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f) runDemo(f);
  };

  const tone = result?.severity && SEVERITY_TONE[result.severity];
  const isVehicle = result?.is_vehicle !== false;

  return (
    <section className="relative py-20 bg-slate-950 text-white overflow-hidden" data-testid="instant-demo">
      <div className="absolute -top-40 -right-40 w-[540px] h-[540px] rounded-full bg-[#003DA5]/40 blur-3xl" />
      <div className="absolute -bottom-40 -left-40 w-[440px] h-[440px] rounded-full bg-[#FF7A00]/25 blur-3xl" />
      <div className="absolute inset-0 dot-grid opacity-30" />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 text-xs font-semibold mb-5">
            <Zap className="w-3.5 h-3.5 text-[#FF7A00]" /> Instant AI demo · No signup required
          </div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter">
            Drop a photo. Get a <span className="gradient-text">₹ estimate</span> in seconds.
          </h2>
          <p className="mt-4 text-slate-300">
            Powered by Gemini Vision. Try it now — no email, no login, no strings. Just the same AI that reviews real claims for our customers.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-6 items-stretch">
          {/* Upload panel */}
          <motion.div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className={`rounded-3xl border-2 border-dashed p-8 transition-colors relative min-h-[360px] flex flex-col items-center justify-center text-center ${
              dragOver ? "border-[#FF7A00] bg-[#FF7A00]/10" : "border-white/20 bg-white/5"
            }`}
            data-testid="instant-demo-drop"
          >
            {preview ? (
              <>
                <img src={preview} alt="upload" className="max-h-[300px] rounded-2xl border border-white/10 shadow-2xl object-contain" />
                <button
                  onClick={reset}
                  className="mt-4 text-xs font-semibold text-slate-300 hover:text-white flex items-center gap-1"
                  data-testid="instant-demo-reset"
                >
                  <RotateCcw className="w-3 h-3" /> Try another
                </button>
              </>
            ) : (
              <>
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#003DA5] to-[#0055D4] flex items-center justify-center mb-4 shadow-lg shadow-[#003DA5]/40">
                  <Camera className="w-8 h-8 text-white" />
                </div>
                <p className="font-heading font-bold text-lg">Drag & drop your accident photo</p>
                <p className="text-sm text-slate-400 mt-1">JPG or PNG, up to 4.5MB</p>
                <label className="mt-5 btn-signature rounded-xl px-6 py-3 font-semibold cursor-pointer inline-flex items-center gap-2" data-testid="instant-demo-upload">
                  <Upload className="w-4 h-4" /> Choose photo
                  <input
                    ref={inputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    onChange={(e) => runDemo(e.target.files?.[0])}
                  />
                </label>
                <p className="text-[10px] text-slate-500 mt-4 max-w-xs">🔒 Photo processed instantly by AI. Not stored. 5 free tries per hour.</p>
              </>
            )}
          </motion.div>

          {/* Result panel */}
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="rounded-3xl bg-white/5 border border-white/10 p-8 relative min-h-[360px] flex flex-col"
          >
            <AnimatePresence mode="wait">
              {loading ? (
                <motion.div key="load" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1 flex flex-col items-center justify-center text-center">
                  <div className="relative">
                    <Loader2 className="w-10 h-10 text-[#FF7A00] animate-spin" />
                    <Sparkles className="w-5 h-5 text-white absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse" />
                  </div>
                  <div className="mt-4 font-heading font-bold">Gemini is analysing…</div>
                  <div className="text-xs text-slate-400 mt-1">Detecting damage, estimating severity & cost</div>
                </motion.div>
              ) : !result ? (
                <motion.div key="idle" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1 flex flex-col items-center justify-center text-center">
                  <Sparkles className="w-10 h-10 text-[#FF7A00] opacity-60" />
                  <div className="mt-3 font-heading font-bold text-lg">Ready for magic</div>
                  <div className="text-sm text-slate-400 mt-1">Upload a photo → AI returns severity, damaged parts, ₹ range & repair days.</div>
                </motion.div>
              ) : !isVehicle ? (
                <motion.div key="notveh" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex-1 flex flex-col items-center justify-center text-center" data-testid="instant-demo-not-vehicle">
                  <div className="w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center mb-3">
                    <Camera className="w-7 h-7 text-amber-400" />
                  </div>
                  <div className="font-heading font-bold text-lg">Hmm, that doesn&apos;t look like a vehicle</div>
                  <div className="text-sm text-slate-400 mt-1 max-w-sm">{result.recommendation}</div>
                </motion.div>
              ) : (
                <motion.div key="result" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex-1 flex flex-col" data-testid="instant-demo-result">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest ${tone.bg} ${tone.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${tone.accent} border`} /> {result.severity}
                    </span>
                    <span className="text-[10px] font-mono text-slate-400">{Math.round((result.confidence || 0) * 100)}% confidence</span>
                  </div>

                  <div className="mt-4">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Estimated cost</div>
                    <div className="font-heading font-black text-3xl sm:text-4xl tracking-tight bg-gradient-to-r from-white via-white to-[#FF7A00] bg-clip-text text-transparent">
                      ₹{(result.estimated_cost_inr_min || 0).toLocaleString("en-IN")} – ₹{(result.estimated_cost_inr_max || 0).toLocaleString("en-IN")}
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-3">
                    <div className="rounded-xl bg-white/5 border border-white/10 p-3">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Repair days</div>
                      <div className="font-heading font-bold text-lg">{result.estimated_days_min}-{result.estimated_days_max} days</div>
                    </div>
                    <div className="rounded-xl bg-white/5 border border-white/10 p-3">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Complexity</div>
                      <div className="font-heading font-bold text-lg">{result.repair_complexity}</div>
                    </div>
                  </div>

                  {result.damaged_parts?.length > 0 && (
                    <div className="mt-3">
                      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5">Damaged parts detected</div>
                      <div className="flex flex-wrap gap-1.5">
                        {result.damaged_parts.map((p) => (
                          <span key={p} className="px-2 py-0.5 rounded-full bg-white/10 border border-white/15 text-[11px] font-semibold" data-testid={`damaged-${p.toLowerCase().replace(/\s+/g,"-")}`}>{p}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-auto pt-4">
                    <Link to="/file-claim" className="btn-signature rounded-xl px-5 py-2.5 font-semibold inline-flex items-center gap-2" data-testid="instant-demo-cta-file">
                      File this claim for real <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
